import type { PagedListReturn } from '@cks-internal/react-base';
import ProjectStatus from 'src/enums/companyProjects/ProjectStatus';
import type {
  MyAssignedProjectContainersResultModel,
  ProjectContainerCompanyGroupModel,
  ProjectContainerListItemModel,
  ProjectStatusGroupModel
} from 'src/features/companyProjects/navigation/types/MyAssignedProjectContainersModels';
import type { ProjectContainerTableModel } from 'src/features/companyProjects/projectContainers/manageContainers/types/ProjectContainerTableModel';
import type { ProjectContainerListEvent } from '../events/projectContainerEventTypes';

const mapStatusStringToEnum = (status: string | undefined): ProjectStatus | undefined => {
  if (!status) return undefined;
  switch (status) {
    case 'Bid':
      return ProjectStatus.Bid;
    case 'Confirmed':
      return ProjectStatus.Confirmed;
    case 'OnHold':
    case 'On Hold':
      return ProjectStatus.OnHold;
    case 'Cancelled':
      return ProjectStatus.Cancelled;
    case 'Completed':
      return ProjectStatus.Completed;
    default:
      return undefined;
  }
};

const cloneStatusGroups = (groups: ProjectStatusGroupModel[]): ProjectStatusGroupModel[] =>
  groups.map((group) => ({
    ...group,
    Companies: cloneAssignedGroups(group.Companies)
  }));

const findStatusGroup = (
  groups: ProjectStatusGroupModel[],
  containerId: number
): { statusIndex: number; groupIndex: number; containerIndex: number } | null => {
  for (let si = 0; si < groups.length; si += 1) {
    const loc = findAssignedContainer(groups[si].Companies, containerId);
    if (loc) return { statusIndex: si, ...loc };
  }
  return null;
};

const cloneAssignedGroups = (groups: ProjectContainerCompanyGroupModel[]): ProjectContainerCompanyGroupModel[] =>
  groups.map((group) => ({
    ...group,
    Containers: group.Containers.map<ProjectContainerListItemModel>((c) => ({ ...c }))
  }));

const findAssignedContainer = (
  groups: ProjectContainerCompanyGroupModel[],
  containerId: number
): { groupIndex: number; containerIndex: number } | null => {
  for (let gi = 0; gi < groups.length; gi += 1) {
    const containers = groups[gi].Containers;
    const ci = containers.findIndex((c) => c.Id === containerId);
    if (ci !== -1) {
      return { groupIndex: gi, containerIndex: ci };
    }
  }
  return null;
};

const ensureAssignedGroup = (
  groups: ProjectContainerCompanyGroupModel[],
  companyId: number
): { groups: ProjectContainerCompanyGroupModel[]; groupIndex: number } => {
  const existingIndex = groups.findIndex((g) => g.CompanyId === companyId);
  if (existingIndex !== -1) {
    return { groups, groupIndex: existingIndex };
  }

  const newGroup: ProjectContainerCompanyGroupModel = {
    CompanyId: companyId,
    CompanyName: '',
    Containers: []
  };

  return {
    groups: [...groups, newGroup],
    groupIndex: groups.length
  };
};

export const applyProjectContainerListEventToAssignedContainersState = (
  current: MyAssignedProjectContainersResultModel | undefined,
  event: ProjectContainerListEvent,
  currentUserId?: string
): MyAssignedProjectContainersResultModel | undefined => {
  if (!current) return current;

  const statusEnum = mapStatusStringToEnum(event.status);
  if (!statusEnum) return current;

  const statusGroups = cloneStatusGroups(current.StatusGroups ?? []);
  const existingLoc = findStatusGroup(statusGroups, event.containerId);
  const isAssignedToCurrentUser =
    !currentUserId ||
    event.assignedToUserId === currentUserId ||
    (event.additionalAssignedToUserIds ?? []).includes(currentUserId);

  const removeExisting = () => {
    if (!existingLoc) return;
    const statusGroup = statusGroups[existingLoc.statusIndex];
    const group = statusGroup.Companies[existingLoc.groupIndex];
    const updatedGroup: ProjectContainerCompanyGroupModel = {
      ...group,
      Containers: group.Containers.filter((_, idx) => idx !== existingLoc.containerIndex)
    };
    statusGroups[existingLoc.statusIndex] = {
      ...statusGroup,
      Companies: statusGroup.Companies.map((g, idx) => (idx === existingLoc.groupIndex ? updatedGroup : g))
    };
  };

  // If we know the current user and this event is for a different assignment set,
  // remove the container from "my assigned" lists.
  if (!isAssignedToCurrentUser) {
    if (!existingLoc) return current;
    removeExisting();

    return {
      ...current,
      StatusGroups: statusGroups
    };
  }

  let existingItem: ProjectContainerListItemModel | undefined;
  if (existingLoc) {
    existingItem =
      statusGroups[existingLoc.statusIndex].Companies[existingLoc.groupIndex].Containers[existingLoc.containerIndex];
    removeExisting();
  }

  const baseItem: ProjectContainerListItemModel = existingItem ?? {
    Id: event.containerId,
    Name: event.name,
    Status: statusEnum,
    CreatedOn: new Date().toISOString(),
    UpdatedOn: null,
    UnreadCount: 0
  };

  const updatedItem: ProjectContainerListItemModel = {
    ...baseItem,
    Name: event.name,
    Status: statusEnum,
    UpdatedOn: new Date().toISOString()
  };

  let targetStatusIndex = statusGroups.findIndex((g) => g.Status === statusEnum);
  if (targetStatusIndex === -1) {
    targetStatusIndex = statusGroups.length;
    statusGroups.push({ Status: statusEnum, Companies: [] });
  }

  const { groups: withGroup, groupIndex } = ensureAssignedGroup(
    statusGroups[targetStatusIndex].Companies,
    event.companyId
  );
  const targetGroup = withGroup[groupIndex];

  const withoutDuplicate = targetGroup.Containers.filter((c) => c.Id !== event.containerId);
  const updatedGroup: ProjectContainerCompanyGroupModel = {
    ...targetGroup,
    Containers: [...withoutDuplicate, updatedItem]
  };

  const nextGroups = [...withGroup];
  nextGroups[groupIndex] = updatedGroup;

  statusGroups[targetStatusIndex] = {
    ...statusGroups[targetStatusIndex],
    Companies: nextGroups
  };

  return {
    ...current,
    StatusGroups: statusGroups
  };
};

export const applyProjectContainerListEventToTablePage = (
  current: PagedListReturn<ProjectContainerTableModel> | undefined,
  event: ProjectContainerListEvent
): PagedListReturn<ProjectContainerTableModel> | undefined => {
  if (!current) return current;

  const statusEnum = mapStatusStringToEnum(event.status);

  const rows = current.PagedRows ?? [];
  const index = rows.findIndex((r) => r.Id === event.containerId);

  if (event.type === 'Updated') {
    if (index === -1) return current;
    const updatedRows = rows.map((row) =>
      row.Id === event.containerId
        ? {
            ...row,
            Name: event.name,
            Status: statusEnum ?? row.Status
          }
        : row
    );
    return { ...current, PagedRows: updatedRows };
  }

  if (event.type === 'Created') {
    if (index !== -1) {
      // Treat as update if it already exists
      const updatedRows = rows.map((row) =>
        row.Id === event.containerId
          ? {
              ...row,
              Name: event.name,
              Status: statusEnum ?? row.Status
            }
          : row
      );
      return { ...current, PagedRows: updatedRows };
    }

    const newRow: ProjectContainerTableModel = {
      Id: event.containerId,
      Name: event.name,
      CompanyId: event.companyId,
      CompanyName: rows[0]?.CompanyName ?? '',
      Status: statusEnum ?? rows[0]?.Status ?? ProjectStatus.Bid,
      AssignedToUserName: null,
      AdditionalAssignedToUserNames: []
    };

    return {
      ...current,
      TotalRows: (current.TotalRows ?? rows.length) + 1,
      PagedRows: [newRow, ...rows]
    };
  }

  return current;
};
